import type { AxiosInstance } from 'axios';
import { BaseService } from './BaseService';
import type {
  PolicyViewModel,
  PolicyCreateModel,
  PolicyUpdateModel,
} from '../types/policy';
import type { ServiceResultT } from '../types/common/ServiceResultT';
import { apiClient } from '../client/client';

export class PolicyService extends BaseService<
  PolicyViewModel,
  PolicyCreateModel,
  PolicyUpdateModel,
  PolicyViewModel[]
> {
  private static readonly BASE = '/api/policies';

  constructor(client: AxiosInstance = apiClient) {
    super(client, PolicyService.BASE);
  }

  async getByRouteSlug(routeSlug: string): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/by-slug/${encodeURIComponent(routeSlug)}`
    );
    return data;
  }

  async getTermsOfService(): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/terms-of-service`
    );
    return data;
  }

  async getPrivacyPolicy(): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/privacy-policy`
    );
    return data;
  }

  async getCookiePolicy(): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/cookie-policy`
    );
    return data;
  }

  async getListingAgreement(): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/listing-agreement`
    );
    return data;
  }

  /** Footer and other callers: active policy links (`GET .../active`). */
  async getActivePolicies(): Promise<ServiceResultT<PolicyViewModel[]>> {
    const { data } = await this.client.get<ServiceResultT<PolicyViewModel[]>>(
      `${PolicyService.BASE}/active`
    );
    return data;
  }

  /** Record user acceptance of an updated policy (`POST .../{id}/accept`). */
  async acceptPolicy(id: number | string): Promise<ServiceResultT<PolicyViewModel>> {
    const { data } = await this.client.post<ServiceResultT<PolicyViewModel>>(
      `${PolicyService.BASE}/${id}/accept`
    );
    return data;
  }
}

export const policyService = new PolicyService();
